import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import "../CSS/ClientMenuModal.css";

const QuickPaymentModal = ({ 
  showModal, 
  setShowModal, 
  updateQuickPayment,
  userId // Add userId as a prop
}) => {
  const [formData, setFormData] = useState({
    name: "",
    details: [],
    price: 500
  });
  const [detailItem, setDetailItem] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Fetch the current quick payment card details
    if (showModal) {
      fetchQuickPaymentDetails();
    }
  }, [showModal, userId]);

  const fetchQuickPaymentDetails = async () => {
    setLoading(true);
    setError("");
    
    const token = localStorage.getItem("adminToken");
    
    try {
      // Include userId in query params if available
      const endpoint = `${process.env.REACT_APP_API_BASE_URL}/api/payment/quick-payment-details${userId ? `?userId=${userId}` : ''}`;
      
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch quick payment details");

      const data = await response.json();
      setFormData({
        name: data.name || "",
        details: data.details || [],
        price: data.price || 500
      });
    } catch (error) {
      setError(error.message);
      // Use default values if fetch fails
      setFormData({
        name: "",
        details: [
          "Express Processing",
          "24-Hour Turnaround",
          "Priority Support",
          "Digital Documentation",
          "Secure Payment Gateway"
        ],
        price: 500
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);
    
    const token = localStorage.getItem("adminToken");
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/update-quick-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          userId: userId // Include userId when updating
        }),
      });

      if (!response.ok) throw new Error("Failed to update quick payment details");

      const data = await response.json();
      setSuccess(true);
      
      // Call the parent function to update state if needed
      if (updateQuickPayment) {
        updateQuickPayment(formData);
      }
      
      // Close modal after short delay
      setTimeout(() => {
        setShowModal(false);
        setSuccess(false);
      }, 1500);
      
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDetail = () => {
    if (detailItem.trim() && formData.details.length < 8) {
      setFormData({
        ...formData,
        details: [...formData.details, detailItem.trim()]
      });
      setDetailItem("");
    }
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = formData.details.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      details: updatedDetails
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "price" ? parseFloat(value) || 0 : value
    });
  };

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold title">Edit Quick Payment Card</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3">
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">Quick payment details updated successfully!</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Card Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter card name"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Price (USD)</Form.Label>
            <Form.Control
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="1"
              step="0.01"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Card Details</Form.Label>
            <div className="d-flex mb-2">
              <Form.Control
                type="text"
                value={detailItem}
                onChange={(e) => setDetailItem(e.target.value)}
                placeholder="Add a detail item"
                className="me-2"
              />
              <Button 
                variant="success" 
                onClick={handleAddDetail}
                disabled={formData.details.length >= 8}
              >
                Add
              </Button>
            </div>
            
            {formData.details.length === 0 && (
              <Alert variant="info">No details added yet. Add some details to display on the card.</Alert>
            )}
            
            <ul className="list-group mt-3">
              {formData.details.map((detail, index) => (
                <li 
                  key={index} 
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  {detail}
                  <Button 
                    variant="outline-danger" 
                    size="sm" 
                    onClick={() => handleRemoveDetail(index)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
            
            {formData.details.length >= 8 && (
              <Alert variant="warning" className="mt-2">
                Maximum of 8 detail items reached.
              </Alert>
            )}
          </Form.Group>
          
          <div className="d-flex justify-content-end mt-4">
            <Button 
              variant="secondary" 
              onClick={() => setShowModal(false)} 
              className="me-2"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              disabled={loading || formData.details.length === 0}
            >
              {loading ? "Updating..." : "Save Changes"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default QuickPaymentModal;
